import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import treatmentAnalyticsService from '../../../services/treatmentAnalyticsService';

const FilterControls = ({ onFiltersChange, onBookmarkSave }) => {
  const [selectedCrops, setSelectedCrops] = useState(['tomatoes', 'peppers']);
  const [selectedFields, setSelectedFields] = useState(['field-a1', 'field-b2']);
  const [treatmentTypes, setTreatmentTypes] = useState(['organic', 'biological']);
  const [comparisonMode, setComparisonMode] = useState('season-over-season');
  const [dateRange, setDateRange] = useState('last-30-days');
  const [effectiveness, setEffectiveness] = useState('all');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const cropOptions = [
    { value: 'tomatoes', label: 'Tomatoes' },
    { value: 'peppers', label: 'Peppers' },
    { value: 'cucumbers', label: 'Cucumbers' },
    { value: 'lettuce', label: 'Lettuce' },
    { value: 'herbs', label: 'Herbs' },
    { value: 'carrots', label: 'Carrots' }
  ];

  const fieldOptions = [
    { value: 'field-a1', label: 'North Field A1 (25.5 acres)' },
    { value: 'field-b2', label: 'East Field B2 (18.2 acres)' },
    { value: 'field-c3', label: 'South Field C3 (32.8 acres)' },
    { value: 'field-d4', label: 'West Field D4 (15.6 acres)' },
    { value: 'field-e5', label: 'Central Field E5 (22.4 acres)' }
  ];

  const treatmentTypeOptions = [
    { value: 'organic', label: 'Organic' },
    { value: 'biological', label: 'Biological' },
    { value: 'chemical', label: 'Chemical' },
    { value: 'mechanical', label: 'Mechanical' },
    { value: 'cultural', label: 'Cultural' }
  ];

  const comparisonOptions = [
    { value: 'season-over-season', label: 'Season over Season' },
    { value: 'year-over-year', label: 'Year over Year' },
    { value: 'month-over-month', label: 'Month over Month' },
    { value: 'treatment-comparison', label: 'Treatment Comparison' }
  ];

  const dateRangeOptions = [
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'current-season', label: 'Current Season' },
    { value: 'last-season', label: 'Last Season' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const effectivenessOptions = [
    { value: 'all', label: 'All Treatments', color: 'bg-gray-100 text-gray-800' },
    { value: 'excellent', label: 'Excellent (90%+)', color: 'bg-success/20 text-success' },
    { value: 'good', label: 'Good (70-89%)', color: 'bg-primary/20 text-primary' },
    { value: 'fair', label: 'Fair (50-69%)', color: 'bg-warning/20 text-warning' },
    { value: 'poor', label: 'Poor (<50%)', color: 'bg-error/20 text-error' }
  ];

  const handleApplyFilters = () => {
    const filters = {
      crops: selectedCrops,
      fields: selectedFields,
      treatmentTypes,
      comparisonMode,
      dateRange,
      effectiveness
    };
    onFiltersChange?.(filters);
  };

  const handleResetFilters = () => {
    setSelectedCrops([]);
    setSelectedFields([]);
    setTreatmentTypes([]);
    setComparisonMode('season-over-season');
    setDateRange('last-30-days');
    setEffectiveness('all');
  };

  const handleExportData = async (format = 'csv') => {
    try {
      setIsExporting(true);
      const filters = {
        crops: selectedCrops,
        fields: selectedFields,
        treatmentTypes,
        dateRange,
        effectiveness
      };
      
      const data = await treatmentAnalyticsService?.exportData(filters, format);
      
      // Create and download file
      const blob = new Blob([data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `treatment-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.${format}`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      window.URL?.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveBookmark = () => {
    const bookmark = {
      name: `Analysis ${new Date()?.toLocaleDateString()}`,
      filters: {
        crops: selectedCrops,
        fields: selectedFields,
        treatmentTypes,
        comparisonMode,
        dateRange,
        effectiveness
      },
      timestamp: new Date()?.toISOString()
    };
    onBookmarkSave?.(bookmark);
  };

  return (
    <div className="bg-card rounded-lg border border-border agricultural-shadow mb-6">
      <div className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-1">
              Analysis Filters
            </h2>
            <p className="text-sm text-muted-foreground">
              Configure your treatment analytics parameters and export data
            </p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
              iconName={showAdvanced ? 'ChevronUp' : 'ChevronDown'}
              iconPosition="right"
            >
              Advanced Filters
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveBookmark}
              iconName="Bookmark"
            >
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetFilters}
              iconName="RotateCcw"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Primary Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <Select
              label="Crop Types"
              options={cropOptions}
              value={selectedCrops}
              onChange={setSelectedCrops}
              multiple
              searchable
              placeholder="Select crops..."
              description="Choose crop types to analyze"
            />
          </div>
          
          <div>
            <Select
              label="Field Zones"
              options={fieldOptions}
              value={selectedFields}
              onChange={setSelectedFields}
              multiple
              searchable
              placeholder="Select fields..."
              description="Choose specific field areas"
            />
          </div>
          
          <div>
            <Select
              label="Comparison Mode"
              options={comparisonOptions}
              value={comparisonMode}
              onChange={setComparisonMode}
              placeholder="Select comparison..."
              description="Choose analysis comparison type"
            />
          </div>
          
          <div>
            <Select
              label="Date Range"
              options={dateRangeOptions}
              value={dateRange}
              onChange={setDateRange}
              placeholder="Select date range..."
              description="Choose time period for analysis"
            />
          </div>
        </div>

        {/* Effectiveness Filter */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-3">
            Treatment Effectiveness
          </label>
          <div className="flex flex-wrap gap-2">
            {effectivenessOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => setEffectiveness(option?.value)}
                className={`
                  px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${effectiveness === option?.value 
                    ? `${option?.color} ring-2 ring-offset-2 ring-current`
                    : 'bg-background border border-border hover:bg-muted'
                  }
                `}
              >
                {option?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Treatment Type Toggles */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-3">
            Treatment Types
          </label>
          <div className="flex flex-wrap gap-3">
            {treatmentTypeOptions?.map((type) => (
              <div key={type?.value} className="flex items-center">
                <Checkbox
                  checked={treatmentTypes?.includes(type?.value)}
                  onChange={(e) => {
                    if (e?.target?.checked) {
                      setTreatmentTypes([...treatmentTypes, type?.value]);
                    } else {
                      setTreatmentTypes(treatmentTypes?.filter(t => t !== type?.value));
                    }
                  }}
                  label={type?.label}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="border-t border-border pt-6">
            <h3 className="text-md font-medium text-foreground mb-4">
              Advanced Parameters
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Select
                  label="Weather Conditions"
                  options={[
                    { value: 'optimal', label: 'Optimal' },
                    { value: 'rainy', label: 'Rainy' },
                    { value: 'windy', label: 'Windy' },
                    { value: 'cloudy', label: 'Cloudy' }
                  ]}
                  placeholder="Filter by weather..."
                  multiple
                />
              </div>
              
              <div>
                <Select
                  label="Application Method"
                  options={[
                    { value: 'spray', label: 'Foliar Spray' },
                    { value: 'drench', label: 'Soil Drench' },
                    { value: 'granular', label: 'Granular' },
                    { value: 'injection', label: 'Trunk Injection' }
                  ]}
                  placeholder="Filter by method..."
                  multiple
                />
              </div>
              
              <div>
                <Select
                  label="Pest Pressure Level"
                  options={[
                    { value: 'low', label: 'Low (0-30%)' },
                    { value: 'medium', label: 'Medium (31-60%)' },
                    { value: 'high', label: 'High (61-100%)' }
                  ]}
                  placeholder="Filter by pressure..."
                  multiple
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <label className="block text-sm font-medium text-foreground">
                  Additional Options
                </label>
                <div className="space-y-2">
                  <Checkbox label="Include organic treatments only" />
                  <Checkbox label="Show cost-effectiveness analysis" />
                  <Checkbox label="Include weather correlation data" />
                  <Checkbox label="Filter by application timing" />
                </div>
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-medium text-foreground">
                  Data Quality Filters
                </label>
                <div className="space-y-2">
                  <Checkbox label="Exclude incomplete records" />
                  <Checkbox label="High confidence data only" />
                  <Checkbox label="Verified measurements only" />
                  <Checkbox label="Include predictive models" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border">
          <Button
            variant="default"
            onClick={handleApplyFilters}
            iconName="Filter"
            iconPosition="left"
          >
            Apply Filters
          </Button>
          
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => handleExportData('csv')}
              iconName="Download"
              iconPosition="left"
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => handleExportData('json')}
              iconName="Download"
              iconPosition="left"
              disabled={isExporting}
            >
              Export JSON
            </Button>
          </div>
          
          <Button
            variant="ghost"
            iconName="Share"
            iconPosition="left"
          >
            Share Analysis
          </Button>
          
          <div className="flex-1" />
          
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} />
            <span>Last updated: {new Date()?.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterControls;